/**
 * CAOS-1585 — the `nameTemplate` grammar, host side: HOW A RECORD TITLES ITSELF.
 *
 * An object may declare `nameTemplate`, a template over the record's own fields that says what is
 * drawn at the top of that record instead of the two rules the renderer used to apply on its own.
 * This file reads one.
 *
 * ── Why a mirror and not a re-export ─────────────────────────────────────────────────────────
 * The same reason `kernel-contract.ts` is a mirror, and it is the established seam here rather
 * than a new one: the host is a separately-deployed package and must typecheck HERMETICALLY
 * against its own sources, never by dragging the kernel's engine sources and their zod/ajv
 * toolchain into a UI bundle's type program. The grammar of record is
 * `kernel/src/meta/name-template.ts`, which the DEPLOY GATE reads; this must stay in lock-step
 * with it. Replace this file with a re-export the day the kernel publishes it.
 *
 * ── The two sides are not symmetric, and the asymmetry is the point ─────────────────────────
 * The kernel side PARSES STRICTLY and refuses anything it cannot read, because refusing at deploy
 * is the whole value of declaring the title instead of coding it. This side is fail-soft: a
 * template that does not parse resolves to nothing and the caller falls back to the documented
 * default. That is not a second opinion about what is valid — it is what a renderer does with a
 * value the gate should already have refused, and drawing a record with braces in its heading, or
 * refusing to draw it at all, would be worse than drawing the title it had yesterday.
 */

/** How many characters of a record id a title shows. The renderer's long-standing blank fallback. */
export const SHORT_ID_LENGTH = 8;

/**
 * Token names the RENDERER answers rather than the record's fields.
 *
 * `id` is the record's short id — the same eight characters the blank-name fallback has always
 * used — and `label` is the object's label as translated for this reader. `label` is what makes
 * the incumbent behaviour writable as a template: the auto-number prefix is `{label} {<nameField>}`
 * and the blank fallback is `{label} {id}`. Without it a template could only hard-code one
 * language's label, and authoring the default explicitly would have been a downgrade.
 */
export const RESERVED_TOKENS = ['id', 'label'] as const;

const RESERVED: ReadonlySet<string> = new Set<string>(RESERVED_TOKENS);

/** Is `token` answered by the renderer rather than by a field of the object? */
export function isReservedToken(token: string): boolean {
  return RESERVED.has(token);
}

/** One piece of a parsed template: literal text, or a token to resolve. */
export type TemplatePart = { kind: 'text'; text: string } | { kind: 'token'; token: string };

const TOKEN_PATTERN = /^[a-zA-Z_][A-Za-z0-9_]*$/;

/**
 * Read a template into its pieces, or `null` if it cannot be read.
 *
 * `null` covers exactly what the kernel gate refuses — a stray brace, an empty `{}`, a token with
 * anything but letters digits and underscores in it, and a template with no token at all — so the
 * two sides agree on which strings are templates. They differ only in what they DO about one that
 * is not: the gate names it and refuses the deploy, this returns null and the caller draws the
 * default.
 */
export function parseTemplate(template: string): TemplatePart[] | null {
  const parts: TemplatePart[] = [];
  let text = '';
  let tokens = 0;
  let i = 0;

  const flush = () => {
    if (text !== '') {
      parts.push({ kind: 'text', text });
      text = '';
    }
  };

  while (i < template.length) {
    const ch = template[i]!;
    if (ch === '}') return null;
    if (ch !== '{') {
      text += ch;
      i += 1;
      continue;
    }
    const close = template.indexOf('}', i + 1);
    if (close === -1) return null;
    const inner = template.slice(i + 1, close);
    if (!TOKEN_PATTERN.test(inner)) return null;
    flush();
    parts.push({ kind: 'token', token: inner });
    tokens += 1;
    i = close + 1;
  }
  flush();
  return tokens === 0 ? null : parts;
}

/**
 * Render a parsed template, asking `valueOf` for each token. `null` means this record has no title
 * of its own and the caller should draw its default.
 *
 * A token with no value contributes nothing and the whitespace around it collapses — the same
 * behaviour a text Formula has, so `{first_name} {last_name}` on a record with no first name reads
 * "Smith" rather than " Smith".
 *
 * ── WHY "NO TOKEN FILLED" AND NOT "THE RESULT IS EMPTY" ─────────────────────────────────────
 * These look equivalent and are not, and the difference is a real record heading. `{name} — {city}`
 * on a record with neither renders "—": non-empty, so a check on the rendered string accepts it,
 * and the record is titled with a bare dash. Worse, EVERY such record is titled with the same bare
 * dash, so the heading stops distinguishing records at all.
 *
 * That is the same fault a template with no tokens has, which the deploy gate refuses outright —
 * the only difference is that this one is refused per RECORD rather than per object, because
 * whether it happens depends on the row. So the rule has to be the same rule: a title is made of
 * what the record supplied, and literal text is the wording around it, never the whole of it.
 */
export function renderTemplate(
  parts: readonly TemplatePart[],
  valueOf: (token: string) => string | null | undefined,
): string | null {
  let out = '';
  let filled = false;
  for (const part of parts) {
    if (part.kind === 'text') {
      out += part.text;
      continue;
    }
    const value = valueOf(part.token);
    if (value == null) continue;
    if (value.trim() !== '') filled = true;
    out += value;
  }
  if (!filled) return null;
  const titled = out.replace(/\s+/g, ' ').trim();
  return titled === '' ? null : titled;
}
